import { Edit3, AlertCircle, CheckCircle2, EyeOff, ChevronDown, ChevronRight, FileText } from 'lucide-react';
import { useState } from 'react';
import IssueLine from './IssueLine';
import { STATUS_LABELS, STATUS_TONES } from '../../utils/findingLabels';
import { formatCents } from '../../utils/formatters';

const STATUS_PILL = {
  AUDIT_PASSED: 'bg-[var(--color-recovery-50)] text-[var(--color-recovery-700)]',
  AUDIT_FLAGGED: 'bg-[var(--color-danger-50)] text-[var(--color-danger-700)]',
  AWAITING_VENDOR_PROFILE: 'bg-[var(--color-action-50)] text-[var(--color-action-700)]',
  AWAITING_DOC_REVIEW: 'bg-[var(--color-insight-50)] text-[var(--color-insight-700)]',
  IGNORED: 'bg-[var(--color-warm-100)] text-[var(--color-warm-600)]',
  PENDING_REVIEW: 'bg-[var(--color-warm-100)] text-[var(--color-warm-600)]',
};

export default function ItemRow({ check, onEdit, onMarkPassed, onMarkIgnored, defaultExpanded = false }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const findings = check.audit_findings || [];
  const invoices = check.invoices || [];
  const ro = check.ro_number_extracted;
  const hasIssues = check.audit_status === 'AUDIT_FLAGGED'
    || check.audit_status === 'AWAITING_VENDOR_PROFILE'
    || check.audit_status === 'AWAITING_DOC_REVIEW';

  return (
    <div className="rounded-lg border border-[var(--color-warm-200)] bg-white overflow-hidden">
      {/* Top row: vendor, check#, status, amount */}
      <div className="flex items-start gap-3 p-4">
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-[var(--color-warm-400)] hover:text-[var(--color-warm-700)] mt-1"
          title={expanded ? 'Collapse' : 'Expand'}
        >
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h3 className="text-[15px] font-semibold text-[var(--color-warm-900)] truncate">
              {check.matched_vendor_name || check.payee || '(Unknown vendor)'}
            </h3>
            <span className="text-[12px] text-[var(--color-warm-500)]">
              Check #{check.check_number || '—'}
            </span>
            {ro && (
              <span className="text-[12px] text-[var(--color-warm-500)]">
                · RO# <span className="font-mono">{ro}</span>
              </span>
            )}
            <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${STATUS_PILL[check.audit_status] || STATUS_PILL.PENDING_REVIEW}`}>
              {STATUS_LABELS[check.audit_status] || check.audit_status}
            </span>
            {invoices.length > 1 && (
              <span className="text-[11px] text-[var(--color-warm-500)] flex items-center gap-1">
                <FileText className="w-3 h-3" />
                {invoices.length} invoices
              </span>
            )}
          </div>
        </div>

        <div className="text-right shrink-0">
          <div className="text-[18px] font-bold text-[var(--color-warm-900)] tabular-nums">
            {formatCents(check.amount_cents)}
          </div>
          {check.folder_date && (
            <div className="text-[11px] text-[var(--color-warm-500)]">
              {new Date(check.folder_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </div>
          )}
        </div>
      </div>

      {/* Findings — show inline for flagged items */}
      {hasIssues && findings.length > 0 && (
        <div className="px-4 pb-3 -mt-1">
          <div className="rounded-md bg-[var(--color-cream-100)] border border-[var(--color-warm-100)] px-3 py-2">
            {findings.map((f, i) => (
              <IssueLine key={i} finding={f} />
            ))}
          </div>
        </div>
      )}

      {/* Expanded: invoice breakdown */}
      {expanded && invoices.length > 0 && (
        <div className="px-4 pb-3 border-t border-[var(--color-warm-100)] pt-3 mt-1 bg-[var(--color-cream-50)]">
          <div className="text-[11px] uppercase tracking-wider text-[var(--color-warm-500)] font-bold mb-2">
            Backing invoices
          </div>
          <div className="space-y-1.5">
            {invoices.map(inv => (
              <div key={inv.id} className="flex items-center gap-3 text-[12px] text-[var(--color-warm-700)]">
                <span className="text-[var(--color-warm-500)]">p{inv.page_index}</span>
                <span className="font-mono">{inv.ro_number_extracted || '—'}</span>
                <span className="flex-1 truncate">{inv.vendor_name_extracted || '—'}</span>
                <span className="font-bold tabular-nums">{formatCents(inv.amount_cents)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action bar */}
      <div className="flex items-center gap-2 px-4 py-2 border-t border-[var(--color-warm-100)] bg-[var(--color-cream-50)]">
        <button
          onClick={() => onEdit?.(check)}
          className="flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-md bg-white border border-[var(--color-warm-200)] hover:border-[var(--color-warm-400)] text-[var(--color-warm-800)]"
        >
          <Edit3 className="w-3.5 h-3.5" />
          Edit
        </button>
        {hasIssues && (
          <button
            onClick={() => onMarkPassed?.(check)}
            className="flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-md text-[var(--color-recovery-700)] hover:bg-[var(--color-recovery-50)]"
          >
            <CheckCircle2 className="w-3.5 h-3.5" />
            Mark passed
          </button>
        )}
        {hasIssues && (
          <button
            onClick={() => onMarkIgnored?.(check)}
            className="flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-md text-[var(--color-warm-600)] hover:bg-[var(--color-warm-100)]"
          >
            <EyeOff className="w-3.5 h-3.5" />
            Ignore
          </button>
        )}
      </div>
    </div>
  );
}
